import React, { useState, useEffect } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Heart } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

interface ContentLikeButtonProps {
  contentType: "image" | "video" | "music"
  contentId: string
  className?: string
}

export function ContentLikeButton({ contentType, contentId, className }: ContentLikeButtonProps) {
  const [isLiked, setIsLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    fetchLikeData()
    checkUserSession()
  }, [contentType, contentId])

  const checkUserSession = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    setUserId(session?.user?.id || null)
  }

  const fetchLikeData = async () => {
    try {
      // Get like count using RPC function (works for everyone)
      const { data: count, error: countError } = await supabase
        .rpc("get_like_count", { 
          content_type_param: contentType, 
          content_id_param: contentId 
        })

      if (countError) throw countError
      setLikeCount(count || 0)

      // Check if current user has liked (only works for authenticated users)
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        const { data: userLike, error: likeError } = await supabase
          .from("content_likes")
          .select("id")
          .eq("content_type", contentType)
          .eq("content_id", contentId)
          .eq("user_id", session.user.id)
          .maybeSingle()

        if (likeError) throw likeError
        setIsLiked(!!userLike)
      }
    } catch (error) {
      console.error("Error fetching like data:", error)
    }
  }

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!userId) {
      toast({
        title: "Sign in required",
        description: "Please sign in to like content",
        variant: "destructive",
      })
      return
    }

    setIsLoading(true)
    try {
      if (isLiked) {
        // Unlike
        const { error } = await supabase
          .from("content_likes")
          .delete()
          .eq("content_type", contentType)
          .eq("content_id", contentId)
          .eq("user_id", userId)

        if (error) throw error
        setIsLiked(false)
        setLikeCount((prev) => Math.max(0, prev - 1))
      } else {
        // Like
        const { error } = await supabase
          .from("content_likes")
          .insert({
            content_type: contentType,
            content_id: contentId,
            user_id: userId,
          })

        if (error) throw error
        setIsLiked(true)
        setLikeCount((prev) => prev + 1)
      }
    } catch (error) {
      console.error("Error toggling like:", error)
      toast({
        title: "Error",
        description: "Failed to update like",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleLike}
      disabled={isLoading}
      className={cn(
        "gap-1.5 transition-all",
        isLiked && "text-red-500 hover:text-red-600",
        className
      )}
    >
      <Heart
        className={cn(
          "h-4 w-4 transition-all",
          isLiked && "fill-current"
        )}
      />
      <span className="text-xs font-medium">{likeCount}</span>
    </Button>
  )
}
